import { type JanusReaders, type Row, actorId, ec, ecLean, handBack, insertRoll, itemField, refuse, requiredInt, scalar, upper } from './context.js';
import type { JanusCtx } from './context.js';
import type { JanusInput } from './janus.js';
import { resolveAttackComposite } from '../actions/attack-weapon-resolution.js';
import { weaponAttackSkill } from '../actions/weapon-attack-skill.js';
import { sourceItemType } from './charged-cast.js';
import { plCatalogs } from '../pl/contract.js';
import { attackRoll } from './rolls/attack-roll.js';
import { reachRefusal } from './templates/template-weapon-attack-roll.js';
import { janusPostRollChangeAbilityResources, preflightPostRollChargeResources } from './charges/adjust-charges.js';
import { findWeaponAmmo, planWeaponAmmo } from '../actions/weapon-attack-inventory.js';
import { explicitWeaponAttackReaders, weaponEnchantmentAttackLayers, type ExplicitWeaponReaders } from '../actions/explicit-weapon-attack.js';
import { computedValue, encounterField, posCoord } from '../../shared/array-types.js';
import { strayCandidates } from './stray-shot.js';

/** The same default/pinned weapon walk as the page gate, for a numeric attack-ability cast. */
export function variableAttackComposite(read: JanusReaders, ability: Row, caster: Row, encounter: Row): Row {
  const resolved = resolveAttackComposite({ ability, caster, weaponInventoryId: null }, {
    value: read.value, entries: read.entries, inventoryField: (row, key) => read.inventoryField?.(row, key), itemField,
    itemType: row => sourceItemType(read, row) ?? String(itemField(row, 'item_type') ?? ''),
    resolveItem: id => read.definition(upper(id)), inCombat: () => read.inCombat(encounter, caster),
  });
  if (!resolved || 'unknown' in resolved) handBack(resolved && 'unknown' in resolved ? resolved.unknown : 'template_weapon_attack_roll:weapon-phase');
  const composite = resolved.composite;
  const sourceId = upper(read.inventoryField?.(composite, 'inventory_item_id') ?? itemField(composite, 'item_id'));
  const source = read.definition(sourceId);
  // Keep a lean inventory reference lean: an items/item_layer_types shell would stop its native template reader.
  return { ...composite, item_type: sourceItemType(read, composite) ?? itemField(composite, 'item_type'),
    item_id: sourceId, item_name: itemField(source, 'item_name') ?? itemField(composite, 'item_name') };
}

/** template_weapon_attack_roll's swing, followed by the restored attack ability's resource settlement.
 * Damage fragments share ONE application, so armor/mitigation/temp V/concentration are never charged twice. */
export function variableAttackRoll(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[]): void {
  const composite = variableAttackComposite(ctx.read, ability, caster, ctx.encounter);
  const ranged = upper(ctx.read.value(composite, 'roll_type')) === '3F85CA97-F9B2-A83A-FDE8-E523FD99346C';
  let weapon: Row = { ...composite, roll_type: ranged ? '3F85CA97-F9B2-A83A-FDE8-E523FD99346C' : '2FE8DFB7-FE61-4862-AF71-3523262350A0' };
  const skill = plCatalogs().skills.getId(weaponAttackSkill(weapon, caster, ctx.read));
  if (!skill) handBack('select_skill_for_contesting_roll:skill-not-held');
  preflightPostRollChargeResources(ctx, ability, caster, input.useAbilityVariable1);
  for (const target of targets) {
    const message = reachRefusal(ctx, caster, target, weapon);
    if (message) refuse(message);
  }
  const plans = new Map<string, Extract<ReturnType<typeof planWeaponAmmo>, { finish: unknown }>>();
  const read: ExplicitWeaponReaders = { value: ctx.read.value, entries: ctx.read.entries,
      inventoryField: (row, key) => ctx.read.inventoryField?.(row, key), itemField,
      itemType: row => sourceItemType(ctx.read, row) ?? String(itemField(row, 'item_type') ?? ''),
      computedValue, resolveItem: id => ctx.read.definition(id) };
  const enchanted = weaponEnchantmentAttackLayers(weapon, read);
  if ('reason' in enchanted) handBack(enchanted.reason);
  weapon = enchanted.weapon;
  if (ctx.read.has(weapon, 'uses_ammo_classification')) {
    const ammo = findWeaponAmmo(caster, weapon, read);
    if (!ammo) handBack('weapon:ranged-ammo-unavailable');
    const statuses = (row: Row): string[] => ctx.read.statuses(row).map(status => upper(scalar(status.status_item_id)));
    for (const target of targets.flatMap(target => [target, ...strayCandidates(ctx, caster, target)])) {
      const plan = planWeaponAmmo(ammo, target, ctx.encounter, upper(encounterField(ctx.encounter, 'encounter_id')), actorId(target), read,
        (row, key) => posCoord(row, key as 'x_pos' | 'y_pos' | 'z_pos'), () => ctx.read.uuid(), statuses);
      if ('reason' in plan) handBack(plan.reason.replace(/^weapon:/, 'weapon:ranged-'));
      if (!plans.size) weapon = plan.weapon(weapon);
      plans.set(actorId(target), plan);
    }
  }
  const phase: { ability: Row; skill: string; hit: boolean; target?: Row } = { ability, skill, hit: false };
  const original = ctx.read;
  const view = explicitWeaponAttackReaders(weapon, caster, read);
  ctx.read = { ...original, entries: view.entries };
  try { attackRoll(ctx, input, weapon, caster, targets, phase); }
  finally { ctx.read = original; }
  const plan = plans.get(actorId(phase.target ?? targets[0]!));
  if (plan) {
    const change = plan.finish(phase.hit, ctx.roll);
    (ctx.weaponInventoryChanges ??= []).push(change);
    insertRoll(ctx, { roll_type_1: change.log_type, roll_item_id: change.inventory_id, by: caster, to: phase.target ?? targets[0] });
  }
  if (ctx.read.inCombat(ctx.encounter, caster)) {
    ec(caster).aps = requiredInt(ecLean(caster, 'aps'), 'template_weapon_attack_roll:aps-not-held') - 1;
    if (!ctx.read.has(ability, 'unhindered_attack'))
      ec(caster).weapon_attack = requiredInt(ecLean(caster, 'weapon_attack'), 'template_weapon_attack_roll:weapon-attack-not-held') + 1;
    if (upper(ctx.read.value(weapon, 'equipment_slot')) === '3805BDD7-3461-DDDB-F445-922A5A992987') ec(caster).head_attack = 1;
  }
  janusPostRollChangeAbilityResources(ctx, ability, caster, input.useAbilityVariable1, phase.hit, input);
}
